import { fetchInvitations } from "app/actions/invitation";
import InvitationModel from "app/models/InvitationModel";
import { useActions, useSelector } from "app/utils/hooks";
import { notificationSortDate } from "app/utils/notificationDate";
import { useCallback, useEffect, useState } from "react";
import useInfiniteScroll from "react-infinite-scroll-hook";
import { shallowEqual } from "react-redux";
import { RootState } from "typedefs";

type GroupNotificationTypes = {
  today: InvitationModel[];
  thisWeek: InvitationModel[];
  thisMonth: InvitationModel[];
  everythingElse: InvitationModel[];
};

export const OutgoingInvitationsModel = () => {
  const invitationAction = useActions({
    fetchInvitations,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState(true);
  const [invitationQuery] = useState<{ [key: string]: any }>({
    perPage: 20,
    direction: "outgoing",
  });
  const outgoingInvitationsData = useSelector(
    (state: RootState) => state.invitation?.outgoingInvitations,
    shallowEqual,
  );

  const [outgoingInvitations, setOutgoingInvitations] = useState<{
    today: InvitationModel[];
    thisWeek: InvitationModel[];
    thisMonth: InvitationModel[];
    everythingElse: InvitationModel[];
  }>({
    today: [],
    thisWeek: [],
    thisMonth: [],
    everythingElse: [],
  });

  const groupNotificationsByDate = useCallback(
    (notifications: InvitationModel[]) => {
      const grouped: GroupNotificationTypes = {
        today: [],
        thisWeek: [],
        thisMonth: [],
        everythingElse: [],
      };

      notifications?.forEach((notification: InvitationModel) => {
        const label = notificationSortDate(
          notification.createdAt,
        )?.toLowerCase();
        if (label === "Today") {
          grouped.today.push(notification);
        } else if (label === "This Week") {
          grouped.thisWeek.push(notification);
        } else if (label === "This Month") {
          grouped.thisMonth.push(notification);
        } else {
          grouped.everythingElse.push(notification);
        }
      });

      return grouped;
    },
    [],
  );

  const loadData = useCallback(
    async (fetchPage?: number) => {
      setLoading(true);
      const res: InvitationModel[] = await invitationAction.fetchInvitations({
        ...invitationQuery,
        page: fetchPage ? fetchPage : currentPage,
      });

      setLoading(false);

      if (fetchPage) {
        setCurrentPage(fetchPage);
      }

      if (res?.length < invitationQuery.perPage) {
        setHasMore(false);
      }

      if (!res) {
        return;
      }

      if (res?.length > 0) {
        const newGroupedNotifications = groupNotificationsByDate(res);
        if (fetchPage) {
          setCurrentPage(fetchPage + 1);
        } else {
          setCurrentPage((prev) => prev + 1);
        }
        setOutgoingInvitations((prev) => ({
          today: [...prev.today, ...newGroupedNotifications.today],
          thisWeek: [...prev.thisWeek, ...newGroupedNotifications.thisWeek],
          thisMonth: [...prev.thisMonth, ...newGroupedNotifications.thisMonth],
          everythingElse: [
            ...prev.everythingElse,
            ...newGroupedNotifications.everythingElse,
          ],
        }));
      }
    },
    [currentPage, invitationQuery, outgoingInvitations],
  );

  const [containerRef] = useInfiniteScroll({
    loading,
    hasNextPage: hasMore,
    onLoadMore: loadData,
    disabled: false,
    rootMargin: "0px 0px 200px 0px",
  });

  const onCancelInvite = () => {
    setHasMore(true);
    setCurrentPage(1);
    setLoading(false);
    setOutgoingInvitations({
      today: [],
      thisWeek: [],
      thisMonth: [],
      everythingElse: [],
    });
    loadData(1);
  };

  useEffect(() => {
    loadData();

    return () => {
      setHasMore(true);
      setCurrentPage(1);
      setLoading(false);
      setOutgoingInvitations({
        today: [],
        thisWeek: [],
        thisMonth: [],
        everythingElse: [],
      });
    };
  }, []);

  return {
    outgoingInvitations,
    containerRef,
    loading,
    onCancelInvite,
    outgoingInvitationsData,
  };
};
